"use client";

import { c as _c } from "react/compiler-runtime";
import { AskCaddieModal } from "@/components/caddie/AskCaddieModal";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GlassIcon } from "@/components/ui/glass-icon";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { VoiceWaveform } from "@/components/voice/VoiceWaveform";
import { CaddieCopilotIntroContent, useB3OSVoiceControl } from "@/components/voice/realtime/B3OSVoiceControl";
import { useGuide } from "@/hooks/useGuide";
import { ASK_CADDIE_LABEL } from "@/lib/caddie";
import { getGuideForRoute } from "@/lib/guides/routeToGuide";
import { openSupport } from "@/lib/support";
import { cn } from "@/lib/utils";
import { useSupportStore } from "@/stores/supportStore";
import { IconNewspaper2, IconSupport } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { IconAlertInfoOutline18, IconArrowRotateClockwiseOutline18, IconChevronRightOutline18, IconKeyboardOutline18, IconMediaStopOutline18, IconMicrophoneOutline18 } from "nucleo-ui-outline-18";
import { Fragment, useState, type ReactNode } from "react";
type SideRailItem = {
  key: string;
  label: string;
  accent: "brand" | "muted";
  icon: ReactNode;
  onClick: () => void;
  active?: boolean;
};
type VoiceRailState = NonNullable<ReturnType<typeof useB3OSVoiceControl>>;
type CaddieDialogMode = "choice" | "voice";
export interface SideRailProps {
  /** Render in-flow (for pages that already manage their own layout) instead of fixed-positioning. */
  inline?: boolean;
  /** Override the ASK CADDIE rail click. Built-in prompt/voice dialogs stay mounted for internal flows. */
  onAskCaddie?: () => void;
  /** Highlight the ASK CADDIE button as active (e.g. when the Caddie panel is open). */
  askCaddieActive?: boolean;
}
export function SideRail(t0) {
  const $ = _c(32);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    inline,
    onAskCaddie,
    askCaddieActive
  } = t1;
  const pathname = usePathname();
  const {
    startGuide
  } = useGuide();
  const openSupportMenu = useSupportStore(_temp);
  const voice = useB3OSVoiceControl();
  const [isAskCaddieOpen, setIsAskCaddieOpen] = useState(false);
  const [caddieDialogMode, setCaddieDialogMode] = useState(null);
  let t2;
  let t3;
  let t4;
  if ($[2] !== askCaddieActive || $[3] !== inline || $[4] !== onAskCaddie || $[5] !== openSupportMenu || $[6] !== pathname || $[7] !== startGuide || $[8] !== voice) {
    const askCaddieLabel = getAskCaddieLabel(voice);
    const items = [{
      key: "ask-caddie",
      label: askCaddieLabel,
      accent: "brand",
      icon: <AskCaddieRailIcon voice={voice} />,
      onClick: onAskCaddie ?? (() => {
        if (!voice) {
          setIsAskCaddieOpen(true);
          return;
        }
        setCaddieDialogMode(voice.isActive ? "voice" : "choice");
      }),
      active: askCaddieActive || voice?.isActive
    }, {
      key: "walkthrough",
      label: "Walkthrough",
      accent: "muted",
      icon: <IconNewspaper2 className="size-3.5 drop-shadow-[0_1px_0_var(--ds-alpha-white-50)]" />,
      onClick: () => {
        const guide = getGuideForRoute(pathname);
        if (guide) {
          startGuide(guide.id);
        } else {
          openSupportMenu("guides");
        }
      }
    }, {
      key: "support",
      label: "Chat with team",
      accent: "muted",
      icon: <IconSupport className="size-3.5 drop-shadow-[0_1px_0_var(--ds-alpha-white-50)]" />,
      onClick: openSupport
    }];
    const t5 = inline ? "relative shrink-0" : "fixed right-2 top-[var(--banner-offset,0px)] z-30 mt-1 h-[calc(100vh-var(--banner-offset,0px))]";
    if ($[12] !== t5) {
      t4 = cn("pointer-events-none hidden h-dvh lg:flex", t5);
      $[12] = t5;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    const t6 = inline ? "pb-2" : "py-2";
    if ($[14] !== t6) {
      t2 = cn("flex flex-col gap-2 px-0.5", t6);
      $[14] = t6;
      $[15] = t2;
    } else {
      t2 = $[15];
    }
    t3 = items.map(item => <Fragment key={item.key}><div className="relative flex justify-end"><button type="button" onClick={item.onClick} aria-label={item.label} aria-pressed={item.active} className={cn("ds-rail-button leading-label hover:bg-ds-surface-hover pointer-events-auto flex flex-col items-center gap-2 rounded-md px-1 py-1.5 text-xs font-medium transition-[background-color,border-color,box-shadow,color,transform,padding,font-size] duration-150 ease-out hover:border-zinc-300 active:scale-[0.98] dark:border-zinc-700 dark:bg-zinc-950 dark:hover:bg-zinc-900", item.accent === "brand" ? "text-ds-brand-600 hover:text-ds-brand-600 dark:text-blue-400" : "text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200", item.active && "!bg-ds-neutral-100 dark:!bg-ds-neutral-800 !text-ds-brand-600 !shadow-ds-press-inset dark:!text-blue-400")}>{item.icon}<span className="tracking-wider [writing-mode:vertical-rl]">{item.label}</span></button>{item.key === "ask-caddie" && voice?.isSpeaking ? <CaddieRailSpeechWaveform analyser={voice.speechAnalyser} /> : null}</div>{item.key === "ask-caddie" && voice?.isActive ? <VoiceRailControls voice={voice} /> : null}</Fragment>);
    $[2] = askCaddieActive;
    $[3] = inline;
    $[4] = onAskCaddie;
    $[5] = openSupportMenu;
    $[6] = pathname;
    $[7] = startGuide;
    $[8] = voice;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
  }
  let t5;
  if ($[16] !== t2 || $[17] !== t3) {
    t5 = <div className={t2}>{t3}</div>;
    $[16] = t2;
    $[17] = t3;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== t4 || $[20] !== t5) {
    t6 = <div className={t4}>{t5}</div>;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== caddieDialogMode || $[23] !== voice) {
    t7 = voice ? <CaddieEntryDialog mode={caddieDialogMode} onModeChange={setCaddieDialogMode} onPromptCaddie={() => {
      setCaddieDialogMode(null);
      setIsAskCaddieOpen(true);
    }} voice={voice} /> : null;
    $[22] = caddieDialogMode;
    $[23] = voice;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = open => {
      setIsAskCaddieOpen(open);
      if (!open) {
        setCaddieDialogMode(null);
      }
    };
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== isAskCaddieOpen) {
    t9 = <AskCaddieModal open={isAskCaddieOpen} onOpenChange={t8} />;
    $[26] = isAskCaddieOpen;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t6 || $[29] !== t7 || $[30] !== t9) {
    t10 = <>{t6}{t7}{t9}</>;
    $[28] = t6;
    $[29] = t7;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
}
function _temp(s) {
  return s.openSupport;
}
function CaddieRailSpeechWaveform(t0) {
  const $ = _c(3);
  const {
    analyser
  } = t0;
  const t1 = Boolean(analyser);
  let t2;
  if ($[0] !== analyser || $[1] !== t1) {
    t2 = <div className="shadow-ds-float-low pointer-events-none absolute right-10 top-2 flex h-7 w-28 items-center rounded-full border border-blue-200/70 bg-white/95 px-2 text-blue-600 shadow-md backdrop-blur-sm dark:border-blue-500/25 dark:bg-zinc-950/95 dark:text-blue-300" aria-hidden={true}><VoiceWaveform analyser={analyser} active={t1} barColor="#2f43ff" barCount={22} /></div>;
    $[0] = analyser;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function CaddieEntryDialog(t0) {
  const $ = _c(12);
  const {
    mode,
    onModeChange,
    onPromptCaddie,
    voice
  } = t0;
  const t1 = mode !== null;
  let t2;
  if ($[0] !== mode || $[1] !== onModeChange) {
    t2 = open => {
      onModeChange(open ? mode ?? "choice" : null);
    };
    $[0] = mode;
    $[1] = onModeChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== mode || $[4] !== onModeChange || $[5] !== onPromptCaddie || $[6] !== voice) {
    t3 = <DialogContent className="max-w-lg" innerClassName="gap-6">{mode === "voice" ? <CaddieCopilotIntroContent connected={voice.connected} connecting={voice.isConnecting} onBack={voice.isActive ? undefined : () => onModeChange("choice")} onCancel={() => onModeChange(null)} onStart={async () => {
        const started = await voice.startVoiceMode();
        if (started) {
          onModeChange(null);
        }
      }} /> : <CaddieChoiceContent onCancel={() => onModeChange(null)} onPromptCaddie={onPromptCaddie} onVoiceMode={() => onModeChange("voice")} />}</DialogContent>;
    $[3] = mode;
    $[4] = onModeChange;
    $[5] = onPromptCaddie;
    $[6] = voice;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = <Dialog open={t1} onOpenChange={t2}>{t3}</Dialog>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
function CaddieChoiceContent(t0) {
  const $ = _c(13);
  const {
    onCancel,
    onPromptCaddie,
    onVoiceMode
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DialogHeader><Image src="/caddie/caddie-default-cropped.svg" alt="" width={64} height={64} className="size-16 object-contain" /><DialogTitle className="pt-2">{ASK_CADDIE_LABEL}</DialogTitle><DialogDescription>Caddie is your AI assistant on B3OS.</DialogDescription></DialogHeader>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onPromptCaddie) {
    t2 = <CaddieChoiceButton description="Type to chat in the Caddie prompt." icon={IconKeyboardOutline18} label="Prompt Caddie" onClick={onPromptCaddie} />;
    $[1] = onPromptCaddie;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onVoiceMode) {
    t3 = <CaddieChoiceButton description="Use your voice to guide the dashboard." icon={IconMicrophoneOutline18} label="Caddie Copilot" onClick={onVoiceMode} />;
    $[3] = onVoiceMode;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <div className="-mx-2 grid gap-1">{t2}{t3}</div>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== onCancel) {
    t5 = <DialogFooter><Button type="button" variant="ghost" size="md" onClick={onCancel}>Not now</Button></DialogFooter>;
    $[8] = onCancel;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = <>{t1}{t4}{t5}</>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}
function CaddieChoiceButton(t0) {
  const $ = _c(14);
  const {
    description,
    icon,
    label,
    onClick
  } = t0;
  let t1;
  if ($[0] !== icon) {
    t1 = <GlassIcon icon={icon} color="blue" size="lg" />;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <span className="text-ds-text-primary block text-base font-semibold">{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== description) {
    t3 = <span className="text-ds-text-secondary mt-1 block text-sm leading-5">{description}</span>;
    $[4] = description;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <span className="min-w-0">{t2}{t3}</span>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <IconChevronRightOutline18 className="text-ds-text-muted ml-auto size-5 shrink-0 transition-transform duration-150 group-hover:translate-x-0.5" />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== onClick || $[11] !== t1 || $[12] !== t4) {
    t6 = <button type="button" onClick={onClick} className="hover:bg-ds-surface-hover focus-visible:bg-ds-surface-hover focus-visible:ring-ds-focus-brand group flex w-full items-center gap-4 rounded-xl px-3 py-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-1">{t1}{t4}{t5}</button>;
    $[10] = onClick;
    $[11] = t1;
    $[12] = t4;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
function getAskCaddieLabel(voice: ReturnType<typeof useB3OSVoiceControl>) {
  if (!voice) return "ASK CADDIE";
  if (voice.isError) return "ERROR";
  if (voice.isConnecting) return "CONNECTING";
  if (voice.isProcessing) return "WORKING";
  if (voice.connected || voice.isListening) return "LISTENING";
  return "ASK CADDIE";
}
function AskCaddieRailIcon(t0) {
  const $ = _c(5);
  const {
    voice
  } = t0;
  if (!voice?.isActive) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Image src="/caddie/caddie-question.svg" alt="" width={16} height={16} className="size-4 scale-125 object-contain" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (voice.isError) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <IconAlertInfoOutline18 className="size-4" />;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <IconMicrophoneOutline18 className="size-4" />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = voice.isListening || voice.isProcessing;
  let t3;
  if ($[3] !== t2) {
    t3 = <span className="relative flex size-4 items-center justify-center">{t1}<span className="b3os-caddie-radar-dot" data-active={t2} /></span>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function VoiceRailControls(t0) {
  const $ = _c(8);
  const {
    voice
  } = t0;
  let t1;
  if ($[0] !== voice) {
    t1 = voice.isError ? <VoiceRailControlButton label="Retry Caddie Copilot" railLabel="RETRY" onClick={() => void voice.startVoiceMode()} tone="brand" icon={<IconArrowRotateClockwiseOutline18 className="size-3.5" />} /> : null;
    $[0] = voice;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <IconMediaStopOutline18 className="size-3.5 fill-current" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== voice.disconnect) {
    t3 = <VoiceRailControlButton label="Stop Caddie Copilot" railLabel="STOP" onClick={voice.disconnect} tone="danger" icon={t2} />;
    $[3] = voice.disconnect;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t3) {
    t4 = <TooltipProvider delayDuration={120}><div className="pointer-events-auto flex flex-col items-center gap-1">{t1}{t3}</div></TooltipProvider>;
    $[5] = t1;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function VoiceRailControlButton(t0) {
  const $ = _c(16);
  const {
    icon,
    label,
    onClick,
    railLabel,
    tone: t1
  } = t0;
  const tone = t1 === undefined ? "muted" : t1;
  const t2 = tone === "brand" && "text-ds-brand-600 hover:text-ds-brand-600 dark:text-blue-400 dark:hover:text-blue-300";
  const t3 = tone === "danger" && "text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300";
  let t4;
  if ($[0] !== t2 || $[1] !== t3) {
    t4 = cn("ds-rail-button leading-label hover:bg-ds-surface-hover flex flex-col items-center justify-center gap-2 rounded-md px-1 py-1.5 text-xs font-medium text-zinc-500 transition-[background-color,border-color,box-shadow,color,transform] duration-150 ease-out hover:border-zinc-300 hover:text-zinc-700 active:scale-[0.98] dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-400 dark:hover:bg-zinc-900 dark:hover:text-zinc-200", t2, t3);
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== railLabel) {
    t5 = <span className="tracking-wider [writing-mode:vertical-rl]">{railLabel}</span>;
    $[3] = railLabel;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== icon || $[6] !== label || $[7] !== onClick || $[8] !== t4 || $[9] !== t5) {
    t6 = <TooltipTrigger asChild={true}><button type="button" onClick={onClick} aria-label={label} className={t4}>{icon}{t5}</button></TooltipTrigger>;
    $[5] = icon;
    $[6] = label;
    $[7] = onClick;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== label) {
    t7 = <TooltipContent side="left">{label}</TooltipContent>;
    $[11] = label;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t6 || $[14] !== t7) {
    t8 = <Tooltip>{t6}{t7}</Tooltip>;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}